
import { useState, useEffect } from "react";
import { cn } from "@/lib/utils";
import { Link } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useIsMobile } from "@/hooks/use-mobile";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [isButtonHovered, setIsButtonHovered] = useState(false);
  const isMobile = useIsMobile();
  
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <header
      className={cn(
        "fixed top-0 left-0 right-0 z-50 py-4 md:py-6 transition-all duration-400",
        scrolled ? "bg-white/95 backdrop-blur-sm shadow-sm" : "bg-transparent"
      )}
    >
      <div className="container-custom flex items-center justify-between">
        <a href="/" className="opacity-90 hover:opacity-100 transition-opacity focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-charcoal/30 rounded-sm">
          <h2 className="text-xl font-serif tracking-wide text-charcoal border-b border-charcoal/30 pb-1">The Monastery Method™</h2>
        </a>
        
        {isMobile ? (
          <MobileNav />
        ) : (
          <DesktopNav isButtonHovered={isButtonHovered} setIsButtonHovered={setIsButtonHovered} />
        )}
      </div>
    </header>
  );
}

function DesktopNav({ 
  isButtonHovered, 
  setIsButtonHovered 
}: { 
  isButtonHovered: boolean; 
  setIsButtonHovered: (value: boolean) => void;
}) {
  return (
    <div className="flex items-center">
      <a 
        href="https://tommyzee.typeform.com/intro" 
        target="_blank" 
        rel="noopener noreferrer" 
        className={`border border-charcoal/80 ${isButtonHovered ? "border-charcoal" : ""} text-charcoal px-3 py-2 text-sm uppercase tracking-widest transition-all duration-300 ${isButtonHovered ? "bg-charcoal text-white shadow-sm translate-y-[-1px]" : "bg-transparent"}`}
        onMouseEnter={() => setIsButtonHovered(true)}
        onMouseLeave={() => setIsButtonHovered(false)}
        aria-label="Start a conversation about The Monastery Method"
      >
        Start The Conversation
      </a>
    </div>
  );
}

function MobileNav() {
  const [open, setOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  
  return (
    <div className="flex items-center">
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetTrigger asChild>
          <button className="p-2 flex items-center justify-center" aria-label="Menu">
            <Menu className="h-6 w-6 text-charcoal" />
          </button>
        </SheetTrigger>
        <SheetContent side="right" className="w-[250px] bg-white py-12">
          <nav className="flex flex-col gap-6">
            <a 
              href="https://tommyzee.typeform.com/intro" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="border border-charcoal/80 text-center text-charcoal px-5 py-2 text-sm uppercase tracking-widest transition-all duration-300"
              aria-label="Start a conversation about The Monastery Method"
            >
              Start The Conversation
            </a>
          </nav>
        </SheetContent>
      </Sheet>
    </div>
  );
}
